import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Users, 
  FileText, 
  LogOut,
  ClipboardList,
  Menu,
  X,
  Eye,
  ArrowLeft,
  Clock,
  TrendingUp,
  DollarSign,
  ChevronRight
} from 'lucide-react';
import { Button } from './ui/button';

const Layout = ({ children }) => {
  const { user, effectiveUser, logout, isAdmin, impersonating, stopImpersonating } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const currentUser = effectiveUser || user;

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  const handleStopImpersonating = () => {
    stopImpersonating();
    navigate('/admin/contractors');
  };

  const showContractorNav = impersonating || !isAdmin();
  const isSalesContractor = currentUser?.contractor_type === 'sales';

  const hourlyContractorNavItems = [
    { path: '/dashboard', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/my-entries', label: 'My Entries', icon: ClipboardList },
  ];

  const salesContractorNavItems = [
    { path: '/sales', label: 'Sales Dashboard', icon: TrendingUp },
  ];

  const adminNavItems = [
    { path: '/admin', label: 'Overview', icon: LayoutDashboard },
    { path: '/admin/timesheets', label: 'Timesheets', icon: Clock },
    { path: '/admin/commissions', label: 'Commissions', icon: DollarSign },
    { path: '/admin/contractors', label: 'Team', icon: Users },
    { path: '/admin/reports', label: 'Reports', icon: FileText },
  ];

  let navItems;
  if (showContractorNav) {
    navItems = isSalesContractor ? salesContractorNavItems : hourlyContractorNavItems;
  } else {
    navItems = adminNavItems;
  }

  const displayUser = impersonating || user;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Impersonation Banner */}
      {impersonating && (
        <div className="bg-brick-cyan text-white px-4 py-2 flex items-center justify-between" data-testid="impersonation-banner">
          <div className="flex items-center gap-2 text-sm font-body">
            <Eye className="w-4 h-4" aria-hidden="true" />
            <span>Viewing as: <strong>{impersonating.name}</strong></span>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleStopImpersonating}
            className="text-white hover:bg-white/20 font-medium"
            data-testid="stop-impersonating-btn"
          >
            <ArrowLeft className="w-4 h-4 mr-1" aria-hidden="true" />
            Exit View
          </Button>
        </div>
      )}

      <div className="flex">
        {/* Desktop Sidebar */}
        <aside className="hidden lg:flex lg:flex-col lg:w-64 lg:fixed lg:inset-y-0 bg-white border-r border-slate-200">
          {/* Logo */}
          <div className="h-16 flex items-center px-6 border-b border-slate-200">
            <Link 
              to={isAdmin() && !impersonating ? '/admin' : '/dashboard'} 
              className="font-heading font-extrabold text-2xl text-slate-900 tracking-tight hover:text-brick-red transition-colors"
            >
              BRICK
            </Link>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-3 py-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  data-testid={`sidebar-nav-${item.label.toLowerCase().replace(' ', '-')}`}
                  className={`flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                    isActive 
                      ? 'bg-slate-100 text-slate-900 border-l-2 border-brick-cyan -ml-[2px] pl-[14px]' 
                      : 'text-slate-600 hover:bg-slate-50 hover:text-slate-900'
                  }`}
                >
                  <Icon className={`w-5 h-5 ${isActive ? 'text-brick-cyan' : ''}`} aria-hidden="true" />
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* User Section */}
          <div className="p-4 border-t border-slate-200">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-9 h-9 rounded-full bg-slate-200 flex items-center justify-center">
                <span className="text-sm font-semibold text-slate-600">
                  {displayUser?.name?.charAt(0)?.toUpperCase()}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-slate-900 truncate">{displayUser?.name}</p>
                <p className="text-xs text-slate-500 uppercase tracking-wide">
                  {impersonating ? 'Contractor View' : displayUser?.role}
                </p>
              </div>
            </div>
            {!impersonating && (
              <Button
                variant="ghost"
                size="sm"
                onClick={handleLogout}
                data-testid="logout-btn"
                className="w-full justify-start text-slate-600 hover:text-brick-red hover:bg-slate-50"
              >
                <LogOut className="w-4 h-4 mr-2" aria-hidden="true" />
                Sign Out
              </Button>
            )}
          </div>
        </aside>

        {/* Mobile Header */}
        <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-xl border-b border-slate-200">
          <div className="flex items-center justify-between h-14 px-4">
            <Link 
              to={isAdmin() && !impersonating ? '/admin' : '/dashboard'} 
              className="font-heading font-extrabold text-xl text-slate-900"
            >
              BRICK
            </Link>
            <button
              className="p-2 text-slate-600 hover:text-slate-900 transition-colors"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-toggle"
            >
              {mobileMenuOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>

          {/* Mobile Nav Dropdown */}
          {mobileMenuOpen && (
            <nav className="bg-white border-t border-slate-200 shadow-lg">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = location.pathname === item.path;
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center justify-between px-4 py-3 border-b border-slate-100 ${
                      isActive 
                        ? 'bg-slate-50 text-brick-cyan' 
                        : 'text-slate-700'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <Icon className="w-5 h-5" aria-hidden="true" />
                      <span className="font-medium">{item.label}</span>
                    </div>
                    <ChevronRight className="w-4 h-4 text-slate-400" aria-hidden="true" />
                  </Link>
                );
              })}
              <div className="px-4 py-3 bg-slate-50">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-sm font-medium text-slate-900">{displayUser?.name}</p>
                    <p className="text-xs text-slate-500 uppercase">{displayUser?.role}</p>
                  </div>
                  {!impersonating && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={handleLogout}
                      className="text-slate-600"
                    >
                      <LogOut className="w-4 h-4" aria-hidden="true" />
                    </Button>
                  )}
                </div>
              </div>
            </nav>
          )}
        </div>

        {/* Main Content */}
        <main className="flex-1 lg:pl-64">
          <div className="pt-14 lg:pt-0">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 lg:py-8">
              {children}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default Layout;
